/**
 * Getting started — strings.
 *
 * SAME PATTERN AS EVIDENCE_STRINGS / INCIDENT_STRINGS / CONTENT_WATCH_STRINGS
 *   ONBOARDING_STRINGS     English; also the inline `defaultValue` of every
 *                          lookup, so the screen renders correctly before the
 *                          locale files are updated.
 *   ONBOARDING_STRINGS_EL  Greek.
 * `registerOnboardingTranslations()` adds both to the running i18n instance
 * without touching i18n/index.ts or the locale JSON, so the parity guard —
 * which reads the JSON — is unaffected. Once merged into en.json and el.json
 * the registration is redundant but harmless: `addResources` never overwrites
 * a key that is already there.
 *
 * ---------------------------------------------------------------------------
 *  THE REGISTER THIS IS WRITTEN IN
 * ---------------------------------------------------------------------------
 * These are professionals discharging a legal obligation. Nothing here
 * celebrates, congratulates or encourages. There is no "Great job!", no streak,
 * no badge and no exclamation mark, because a compliance tool that cheers at
 * someone is a compliance tool they stop taking seriously — and because the
 * person reading this may well be behind on something that carries a fine.
 *
 * Every `why` line answers "why should I care", in one sentence, in the terms
 * the obligation is actually written in. "It's a good idea" is not a reason;
 * "a blank justification is the finding auditors raise most often" is.
 *
 * ---------------------------------------------------------------------------
 *  WHY THE PROSE IS HERE AND NOT IN THE API RESPONSE
 * ---------------------------------------------------------------------------
 * The server returns a step key, a state, a link and the numbers. It returns no
 * English at all. That is what lets a Greek user read a Greek checklist without
 * the API knowing anything about it, and it keeps the facts (the server's job)
 * separate from the wording (a translator's).
 */

import i18n from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const ONBOARDING_STRINGS = {
  // --- Navigation (for whoever adds the Shell entry) ------------------------
  'nav.gettingStarted': 'Getting started',
  'nav.shortGettingStarted': 'Start',

  // --- Page chrome ---------------------------------------------------------
  'onboarding.title': 'Getting started',
  'onboarding.subtitle':
    'What this organisation is working towards, and what is still outstanding. Every tick below is read from your records, not stored — it changes as the work does.',
  'onboarding.loadFailed': 'Could not load your checklist.',
  'onboarding.retry': 'Try again',
  'onboarding.open': 'Open',
  'onboarding.viewAll': 'See the full checklist',
  'onboarding.progress': '{{done}} of {{total}} done',
  'onboarding.noSteps':
    'There is nothing on this list for your role. That is not an oversight — the checklist only shows work you are allowed to do.',

  // --- The goal question ---------------------------------------------------
  'onboarding.ask.title': 'What are you trying to achieve?',
  'onboarding.ask.intro':
    'There are a lot of screens here and they are not all for you. Tell us the job, and we will put the parts that matter in order. You can change this at any time, and the honest answer in six months is often a different one.',
  'onboarding.ask.skip': 'Skip for now',
  'onboarding.ask.save': 'Continue',
  'onboarding.ask.otherGoals': 'Something else',
  'onboarding.ask.readOnly':
    'Only an administrator of this organisation can set this.',
  'onboarding.ask.dismissed':
    'You put this away earlier. Nothing has been lost — pick a goal whenever you want.',

  'onboarding.goal.current': 'Working towards',
  'onboarding.goal.change': 'Change',
  'onboarding.goal.cancel': 'Cancel',
  'onboarding.goal.unset': 'No goal chosen',
  'onboarding.goal.unsetBody':
    'Nobody has said what this organisation is working towards, so the checklist below is the general one.',

  'onboarding.goal.NIS2.title': 'Comply with NIS2',
  'onboarding.goal.NIS2.body':
    'We are in scope for the directive and have to be able to show it.',
  'onboarding.goal.ISO27001.title': 'Get ISO 27001 certified',
  'onboarding.goal.ISO27001.body':
    'An auditor is coming. The Statement of Applicability is the document they ask for first.',
  'onboarding.goal.DORA.title': 'Meet DORA',
  'onboarding.goal.DORA.body':
    'We are a financial entity, so the register of information and ICT third-party risk dominate.',
  'onboarding.goal.GDPR.title': 'Be defensible on GDPR',
  'onboarding.goal.GDPR.body':
    'We hold personal data and need to be able to show how it is protected.',
  'onboarding.goal.CUSTOMER_ASSURANCE.title': 'Answer customer security questionnaires',
  'onboarding.goal.CUSTOMER_ASSURANCE.body':
    'Our customers keep sending us spreadsheets. We would rather send them a page.',
  'onboarding.goal.SUPPLY_CHAIN.title': 'Get our suppliers under control',
  'onboarding.goal.SUPPLY_CHAIN.body':
    'We need to know who we depend on and how much exposure they bring with them.',
  'onboarding.goal.MANAGE_CLIENTS.title': 'Manage compliance for our clients',
  'onboarding.goal.MANAGE_CLIENTS.body':
    'We run other organisations’ programmes. Our own assessment is not the job.',
  'onboarding.goal.EXPLORING.title': 'Not sure yet — show me around',
  'onboarding.goal.EXPLORING.body':
    'We will pick a direction once we have seen what is here.',

  // --- The single next action ----------------------------------------------
  // "11 items outstanding" stops people. One sentence naming one thing moves
  // them, which is why this gets its own card above the list.
  'onboarding.next.title': 'Do this next',
  'onboarding.next.allBlocked':
    'Everything left is waiting on something else. Finish the step above it first.',

  'onboarding.complete.title': 'Nothing outstanding',
  'onboarding.complete.body':
    'Every step on this list is finished. It stays here, and it will pick things up again if a licence lapses, a new gap opens or evidence expires.',

  // --- States --------------------------------------------------------------
  'onboarding.state.DONE': 'Done',
  'onboarding.state.PARTIAL': 'In progress',
  'onboarding.state.TODO': 'To do',
  'onboarding.state.BLOCKED': 'Waiting',
  'onboarding.blockedBy': 'Waiting on “{{step}}” above.',

  // --- Dismissal -----------------------------------------------------------
  'onboarding.dismiss': 'Hide this',
  'onboarding.restore': 'Show the checklist again',
  'onboarding.hidden.title': 'This checklist is hidden',
  'onboarding.hidden.body':
    'You hid it, so it no longer appears on your dashboard. It is still here whenever you want it.',

  // --- Steps: titles and why -----------------------------------------------
  'onboarding.step.framework.license.title': 'Licence {{framework}}',
  'onboarding.step.framework.license.titleAny': 'Licence a framework',
  'onboarding.step.framework.license.why':
    'A licence is what opens that framework’s questionnaire and its requirements. Nothing downstream of it can start until one is in place.',
  'onboarding.step.framework.license.licensed': '{{framework}} is licensed.',
  'onboarding.step.framework.license.licensedCount_one': '{{count}} framework licensed.',
  'onboarding.step.framework.license.licensedCount_other': '{{count}} frameworks licensed.',

  'onboarding.step.assessment.complete.title': 'Complete the {{framework}} assessment',
  'onboarding.step.assessment.complete.titleAny': 'Complete an assessment',
  'onboarding.step.assessment.complete.why':
    'Finalising it is what generates your gap list. Until then the product has nothing to tell you about where you stand.',
  'onboarding.step.assessment.complete.finalized':
    'Finalised — {{rate}}% compliant.',
  'onboarding.step.assessment.complete.inProgress':
    '{{answered}} of {{total}} answered. {{remaining}} to go, then finalise it.',
  'onboarding.step.assessment.complete.notStarted_one':
    '{{count}} question, none answered yet.',
  'onboarding.step.assessment.complete.notStarted_other':
    '{{count}} questions, none answered yet.',

  'onboarding.step.gaps.plan.title': 'Put a plan against every gap',
  'onboarding.step.gaps.plan.why':
    'A gap nobody owns is the one still open a year from now. A plan names who is doing what, and by when.',
  'onboarding.step.gaps.plan.unplanned_one':
    '{{unplanned}} of {{open}} open gaps has no plan against it.',
  'onboarding.step.gaps.plan.unplanned_other':
    '{{unplanned}} of {{open}} open gaps have no plan against them.',
  'onboarding.step.gaps.plan.allPlanned_one': 'The one open gap is on a plan.',
  'onboarding.step.gaps.plan.allPlanned_other': 'All {{count}} open gaps are on a plan.',
  'onboarding.step.gaps.plan.noOpenGaps': 'No open gaps to plan for.',

  'onboarding.step.gaps.close.title': 'Close your gaps',
  'onboarding.step.gaps.close.why':
    'This is the work itself. A gap closes when every plan against it is done — the product will not close one for you.',
  'onboarding.step.gaps.close.openGaps_one': '{{count}} gap still open.',
  'onboarding.step.gaps.close.openGaps_other': '{{count}} gaps still open.',
  'onboarding.step.gaps.close.someClosed_one':
    '{{closed}} closed, {{open}} still open.',
  'onboarding.step.gaps.close.someClosed_other':
    '{{closed}} closed, {{open}} still open.',
  'onboarding.step.gaps.close.allClosed_one': 'The one gap found has been closed.',
  'onboarding.step.gaps.close.allClosed_other': 'All {{count}} gaps found have been closed.',

  'onboarding.step.policies.create.title': 'Write your first policy',
  'onboarding.step.policies.create.why':
    'Documented policies are asked for by name in NIS2 Article 21 and ISO 27001 Annex A. Generate one from a template rather than starting at a blank page.',
  'onboarding.step.policies.create.activePolicies_one': '{{count}} active policy.',
  'onboarding.step.policies.create.activePolicies_other': '{{count}} active policies.',

  'onboarding.step.evidence.attach.title': 'Attach evidence to your answers',
  'onboarding.step.evidence.attach.why':
    'An answer with nothing behind it is an assertion. Evidence is what turns it into something an auditor will accept — and the product tracks when it expires.',
  'onboarding.step.evidence.attach.evidenceCount_one': '{{count}} piece of evidence attached.',
  'onboarding.step.evidence.attach.evidenceCount_other': '{{count}} pieces of evidence attached.',

  'onboarding.step.soa.scope.title': 'Write your ISMS scope statement',
  'onboarding.step.soa.scope.why':
    'A Statement of Applicability that does not say what it is the applicability of is not a standalone document. One or two sentences is enough.',
  'onboarding.step.soa.scope.scopeWritten': 'Scope statement recorded.',

  'onboarding.step.soa.decide.title': 'Decide and justify every Annex A control',
  'onboarding.step.soa.decide.why':
    'Clause 6.1.3(d). No certificate is issued without this document, and a decision recorded with a blank justification is the finding auditors raise most often.',
  'onboarding.step.soa.decide.soaNone_one': '{{count}} control, none decided yet.',
  'onboarding.step.soa.decide.soaNone_other': '{{count}} controls, none decided yet.',
  'onboarding.step.soa.decide.soaUndecided_one':
    '{{decided}} of {{total}} decided — {{undecided}} still undecided.',
  'onboarding.step.soa.decide.soaUndecided_other':
    '{{decided}} of {{total}} decided — {{undecided}} still undecided.',
  'onboarding.step.soa.decide.soaUnjustified_one':
    'All {{total}} decided, but {{unjustified}} has no written justification.',
  'onboarding.step.soa.decide.soaUnjustified_other':
    'All {{total}} decided, but {{unjustified}} have no written justification.',
  'onboarding.step.soa.decide.soaDone_one': '{{count}} control decided and justified.',
  'onboarding.step.soa.decide.soaDone_other': 'All {{count}} controls decided and justified.',

  'onboarding.step.trust.publish.title': 'Publish your Trust Centre',
  'onboarding.step.trust.publish.why':
    'A page you can send instead of filling in the next questionnaire by hand. You choose exactly what it shows, and it stays current on its own.',
  'onboarding.step.trust.publish.trustLive': 'Live at /trust/{{slug}}',
  'onboarding.step.trust.publish.trustDraft': 'Set up, but not published yet.',

  'onboarding.step.vendors.add.title': 'List the suppliers you depend on',
  'onboarding.step.vendors.add.why':
    'You cannot assess, score or monitor a supplier the product has never been told about.',
  'onboarding.step.vendors.add.vendorCount_one': '{{count}} active supplier.',
  'onboarding.step.vendors.add.vendorCount_other': '{{count}} active suppliers.',

  'onboarding.step.vendors.assess.title': 'Send a questionnaire to your critical suppliers',
  'onboarding.step.vendors.assess.why':
    'Their answers are what your own supply-chain obligations rest on. They answer through a link; they need no account here.',
  'onboarding.step.vendors.assess.vendorAssessDone_one': '{{count}} questionnaire answered.',
  'onboarding.step.vendors.assess.vendorAssessDone_other': '{{count}} questionnaires answered.',
  'onboarding.step.vendors.assess.vendorAssessAwaiting_one':
    '{{count}} sent, nothing back yet.',
  'onboarding.step.vendors.assess.vendorAssessAwaiting_other':
    '{{count}} sent, nothing back yet.',
  'onboarding.step.vendors.assess.vendorAssessMixed_one':
    '{{answered}} answered, {{awaiting}} still outstanding.',
  'onboarding.step.vendors.assess.vendorAssessMixed_other':
    '{{answered}} answered, {{awaiting}} still outstanding.',

  'onboarding.step.scans.run.title': 'Run an external check on a domain',
  'onboarding.step.scans.run.why':
    'A passive look at what a domain exposes to the internet — certificates, headers, DNS. It costs credits, so start with one that matters.',
  'onboarding.step.scans.run.scansReady_one': '{{count}} report ready.',
  'onboarding.step.scans.run.scansReady_other': '{{count}} reports ready.',
  'onboarding.step.scans.run.scansRunning_one': '{{count}} scan running.',
  'onboarding.step.scans.run.scansRunning_other': '{{count}} scans running.',

  'onboarding.step.clients.create.title': 'Add your first client organisation',
  'onboarding.step.clients.create.why':
    'Almost everything you do here happens inside a client’s tenancy rather than your own, so this comes before anything else.',
  'onboarding.step.clients.create.clientCount_one': '{{count}} client organisation.',
  'onboarding.step.clients.create.clientCount_other': '{{count}} client organisations.',

  'onboarding.step.clients.consent.title': 'Get each client’s consent to see their data',
  'onboarding.step.clients.consent.why':
    'Sitting above a client in the channel lets you bill them. It does not let you read their assessments, gaps or evidence — they grant that themselves, and they can withdraw it.',
  'onboarding.step.clients.consent.consentAll_one': 'Your one client has consented.',
  'onboarding.step.clients.consent.consentAll_other': 'All {{count}} clients have consented.',
  'onboarding.step.clients.consent.consentMissing_one':
    '{{consented}} of {{total}} have consented — {{missing}} has not.',
  'onboarding.step.clients.consent.consentMissing_other':
    '{{consented}} of {{total}} have consented — {{missing}} have not.',

  'onboarding.step.clients.credits.title': 'Allocate credits to your clients',
  'onboarding.step.clients.credits.why':
    'Framework licences, supplier questionnaires and scans are all paid for in credits. A client with none cannot start.',
  'onboarding.step.clients.credits.creditsAll_one': 'Your one client has credits.',
  'onboarding.step.clients.credits.creditsAll_other': 'All {{count}} clients have credits.',
  'onboarding.step.clients.credits.creditsMissing_one':
    '{{funded}} of {{total}} funded — {{missing}} has no credits.',
  'onboarding.step.clients.credits.creditsMissing_other':
    '{{funded}} of {{total}} funded — {{missing}} have no credits.',

  'onboarding.step.team.invite.title': 'Invite the people who will do the work',
  'onboarding.step.team.invite.why':
    'Compliance is not one person’s job. Plans need owners and policies need approvers, and neither can be assigned to somebody who is not here.',
  'onboarding.step.team.invite.teamCount_one': 'You are the only person here.',
  'onboarding.step.team.invite.teamCount_other': '{{count}} people in this organisation.',
  'onboarding.step.team.invite.teamAlone': 'You are the only person here.',

  'onboarding.step.account.totp.title': 'Move to an authenticator app',
  'onboarding.step.account.totp.why':
    'Your account already has a second factor — codes sent by email. An authenticator app is the stronger of the two and still works when mail does not.',
  'onboarding.step.account.totp.totpOn': 'Authenticator app in use.',
  'onboarding.step.account.totp.totpEmail': 'Currently using codes sent by email.',

  // --- References ----------------------------------------------------------
  // Links worth knowing about that are deliberately NOT checklist items,
  // because nothing in the database could honestly tick them off. Keeping an
  // untickable step on the list would stop the progress figure ever reaching
  // its end, which is the cheap trick this feature is meant not to play.
  'onboarding.references.title': 'Worth knowing about',
  'onboarding.references.note': 'Not part of the checklist — just where these live.',
  'onboarding.reference.incidents.title': 'Where an incident goes',
  'onboarding.reference.incidents.body':
    'NIS2 Article 23 allows 24 hours for an early warning. Finding the screen beforehand is the point of this line.',
  'onboarding.reference.catalogue.title': 'Prove once, satisfy many',
  'onboarding.reference.catalogue.body':
    'One control can answer requirements across several frameworks. The control library shows which, and what evidence you already hold.',
  'onboarding.reference.reports.title': 'Exports for your auditor',
  'onboarding.reference.reports.body':
    'The Statement of Applicability and the assessment reports download from here.',
  'onboarding.reference.vendorAssessments.title': 'Questionnaires you have sent',
  'onboarding.reference.vendorAssessments.body':
    'What you have asked suppliers, what has come back, and what has expired.',
} as const;

export type OnboardingKey = keyof typeof ONBOARDING_STRINGS;

export const ONBOARDING_STRINGS_EL: Record<OnboardingKey, string> = {
  'nav.gettingStarted': 'Πρώτα βήματα',
  'nav.shortGettingStarted': 'Βήματα',

  'onboarding.title': 'Πρώτα βήματα',
  'onboarding.subtitle':
    'Τι επιδιώκει ο οργανισμός και τι εκκρεμεί. Κάθε βήμα παρακάτω υπολογίζεται από τα ίδια τα δεδομένα σας — δεν αποθηκεύεται, αλλάζει όπως αλλάζει η δουλειά.',
  'onboarding.loadFailed':
    'Δεν ήταν δυνατή η φόρτωση της λίστας.',
  'onboarding.retry': 'Δοκιμάστε ξανά',
  'onboarding.open': 'Άνοιγμα',
  'onboarding.viewAll': 'Δείτε όλη τη λίστα',
  'onboarding.progress': '{{done}} από {{total}} ολοκληρώθηκαν',
  'onboarding.noSteps':
    'Δεν υπάρχει κάτι σε αυτή τη λίστα για τον ρόλο σας. Δεν πρόκειται για παράλειψη — η λίστα δείχνει μόνο εργασίες που επιτρέπεται να κάνετε.',

  'onboarding.ask.title': 'Τι θέλετε να πετύχετε;',
  'onboarding.ask.intro':
    'Οι οθόνες εδώ είναι πολλές και δεν αφορούν όλες εσάς. Πείτε μας τη δουλειά και θα βάλουμε σε σειρά αυτά που μετράνε. Μπορείτε να το αλλάξετε οποτεδήποτε — η ειλικρινής απάντηση σε έξι μήνες συχνά είναι διαφορετική.',
  'onboarding.ask.skip': 'Αργότερα',
  'onboarding.ask.save': 'Συνέχεια',
  'onboarding.ask.otherGoals': 'Κάτι άλλο',
  'onboarding.ask.readOnly':
    'Μόνο διαχειριστής του οργανισμού μπορεί να το ορίσει.',
  'onboarding.ask.dismissed':
    'Το αποκρύψατε νωρίτερα. Τίποτα δεν χάθηκε — διαλέξτε στόχο όποτε θέλετε.',

  'onboarding.goal.current': 'Στόχος',
  'onboarding.goal.change': 'Αλλαγή',
  'onboarding.goal.cancel': 'Άκυρο',
  'onboarding.goal.unset': 'Δεν έχει επιλεγεί στόχος',
  'onboarding.goal.unsetBody':
    'Κανείς δεν έχει δηλώσει τι επιδιώκει ο οργανισμός, οπότε η λίστα παρακάτω είναι η γενική.',

  'onboarding.goal.NIS2.title': 'Συμμόρφωση με την NIS2',
  'onboarding.goal.NIS2.body':
    'Εμπίπτουμε στην οδηγία και πρέπει να μπορούμε να το αποδείξουμε.',
  'onboarding.goal.ISO27001.title': 'Πιστοποίηση ISO 27001',
  'onboarding.goal.ISO27001.body':
    'Έρχεται ελεγκτής. Το Πεδίο Εφαρμογής (SoA) είναι το πρώτο έγγραφο που ζητά.',
  'onboarding.goal.DORA.title': 'Συμμόρφωση με τον DORA',
  'onboarding.goal.DORA.body':
    'Είμαστε χρηματοοικονομική οντότητα, άρα προτεραιότητα έχουν το μητρώο πληροφοριών και ο κίνδυνος τρίτων.',
  'onboarding.goal.GDPR.title': 'Θωρακισμός στον GDPR',
  'onboarding.goal.GDPR.body':
    'Τηρούμε προσωπικά δεδομένα και πρέπει να δείξουμε πώς προστατεύονται.',
  'onboarding.goal.CUSTOMER_ASSURANCE.title':
    'Απαντήσεις σε ερωτηματολόγια πελατών',
  'onboarding.goal.CUSTOMER_ASSURANCE.body':
    'Οι πελάτες μας στέλνουν συνέχεια φύλλα Excel. Προτιμούμε να τους στέλνουμε μια σελίδα.',
  'onboarding.goal.SUPPLY_CHAIN.title':
    'Έλεγχος των προμηθευτών μας',
  'onboarding.goal.SUPPLY_CHAIN.body':
    'Πρέπει να ξέρουμε από ποιους εξαρτώμαστε και πόση έκθεση μας φέρνουν.',
  'onboarding.goal.MANAGE_CLIENTS.title':
    'Διαχείριση συμμόρφωσης για πελάτες μας',
  'onboarding.goal.MANAGE_CLIENTS.body':
    'Τρέχουμε τα προγράμματα άλλων οργανισμών. Η δική μας αξιολόγηση δεν είναι το ζητούμενο.',
  'onboarding.goal.EXPLORING.title':
    'Ακόμα δεν ξέρω — δείξτε μου το περιβάλλον',
  'onboarding.goal.EXPLORING.body':
    'Θα διαλέξουμε κατεύθυνση αφού δούμε τι υπάρχει εδώ.',

  'onboarding.next.title': 'Το επόμενο βήμα',
  'onboarding.next.allBlocked':
    'Ότι απομένει περιμένει κάτι άλλο. Ολοκληρώστε πρώτα το προηγούμενο βήμα.',

  'onboarding.complete.title': 'Δεν εκκρεμεί κάτι',
  'onboarding.complete.body':
    'Κάθε βήμα σε αυτή τη λίστα έχει ολοκληρωθεί. Η λίστα παραμένει εδώ και θα το επισημάνει αν λήξει μια άδεια, ανοίξει νέο κενό ή λήξει ένα τεκμήριο.',

  'onboarding.state.DONE': 'Ολοκληρώθηκε',
  'onboarding.state.PARTIAL': 'Σε εξέλιξη',
  'onboarding.state.TODO': 'Εκκρεμεί',
  'onboarding.state.BLOCKED': 'Αναμονή',
  'onboarding.blockedBy':
    'Περιμένει το βήμα «{{step}}» παραπάνω.',

  'onboarding.dismiss': 'Απόκρυψη',
  'onboarding.restore': 'Εμφάνιση ξανά',
  'onboarding.hidden.title': 'Η λίστα είναι κρυμμένη',
  'onboarding.hidden.body':
    'Την αποκρύψατε, οπότε δεν εμφανίζεται πλέον στον πίνακα. Παραμένει εδώ όποτε τη χρειαστείτε.',

  'onboarding.step.framework.license.title': 'Άδεια για {{framework}}',
  'onboarding.step.framework.license.titleAny':
    'Άδεια για ένα πλαίσιο',
  'onboarding.step.framework.license.why':
    'Η άδεια ξεκλειδώνει το ερωτηματολόγιο και τις απαιτήσεις του πλαισίου. Τίποτα από τα επόμενα δεν ξεκινά χωρίς αυτή.',
  'onboarding.step.framework.license.licensed':
    'Το {{framework}} έχει άδεια.',
  'onboarding.step.framework.license.licensedCount_one':
    '{{count}} πλαίσιο με άδεια.',
  'onboarding.step.framework.license.licensedCount_other':
    '{{count}} πλαίσια με άδεια.',

  'onboarding.step.assessment.complete.title':
    'Ολοκληρώστε την αξιολόγηση {{framework}}',
  'onboarding.step.assessment.complete.titleAny':
    'Ολοκληρώστε μια αξιολόγηση',
  'onboarding.step.assessment.complete.why':
    'Η οριστικοποίησή της παράγει τα κενά σας. Μέχρι τότε το σύστημα δεν έχει κάτι να σας πει για τη θέση σας.',
  'onboarding.step.assessment.complete.finalized':
    'Οριστικοποιήθηκε — {{rate}}% συμμόρφωση.',
  'onboarding.step.assessment.complete.inProgress':
    '{{answered}} από {{total}} απαντήθηκαν. Μένουν {{remaining}}, και μετά οριστικοποίησή της.',
  'onboarding.step.assessment.complete.notStarted_one':
    '{{count}} ερώτηση, καμία απάντηση ακόμα.',
  'onboarding.step.assessment.complete.notStarted_other':
    '{{count}} ερωτήσεις, καμία απάντηση ακόμα.',

  'onboarding.step.gaps.plan.title':
    'Βάλτε σχέδιο σε κάθε κενό',
  'onboarding.step.gaps.plan.why':
    'Ένα κενό χωρίς υπεύθυνο είναι αυτό που θα είναι ακόμα ανοιχτό σε ένα χρόνο. Το σχέδιο ορίζει ποιος κάνει τι, και ως πότε.',
  'onboarding.step.gaps.plan.unplanned_one':
    '{{unplanned}} από τα {{open}} ανοιχτά κενά δεν έχει σχέδιο.',
  'onboarding.step.gaps.plan.unplanned_other':
    '{{unplanned}} από τα {{open}} ανοιχτά κενά δεν έχουν σχέδιο.',
  'onboarding.step.gaps.plan.allPlanned_one':
    'Το μοναδικό ανοιχτό κενό έχει σχέδιο.',
  'onboarding.step.gaps.plan.allPlanned_other':
    'Και τα {{count}} ανοιχτά κενά έχουν σχέδιο.',
  'onboarding.step.gaps.plan.noOpenGaps':
    'Δεν υπάρχουν ανοιχτά κενά.',

  'onboarding.step.gaps.close.title': 'Κλείστε τα κενά σας',
  'onboarding.step.gaps.close.why':
    'Εδώ είναι η ουσιαστική δουλειά. Ένα κενό κλείνει όταν κάθε σχέδιο που το αφορά ολοκληρωθεί.',
  'onboarding.step.gaps.close.openGaps_one':
    '{{count}} κενό ακόμα ανοιχτό.',
  'onboarding.step.gaps.close.openGaps_other':
    '{{count}} κενά ακόμα ανοιχτά.',
  'onboarding.step.gaps.close.someClosed_one':
    '{{closed}} έκλεισαν, {{open}} ακόμα ανοιχτό.',
  'onboarding.step.gaps.close.someClosed_other':
    '{{closed}} έκλεισαν, {{open}} ακόμα ανοιχτά.',
  'onboarding.step.gaps.close.allClosed_one':
    'Το μοναδικό κενό έκλεισε.',
  'onboarding.step.gaps.close.allClosed_other':
    'Και τα {{count}} κενά έκλεισαν.',

  'onboarding.step.policies.create.title':
    'Γράψτε την πρώτη σας πολιτική',
  'onboarding.step.policies.create.why':
    'Οι τεκμηριωμένες πολιτικές ζητούνται ρητά από το άρθρο 21 της NIS2 και το Παράρτημα A του ISO 27001. Ξεκινήστε από πρότυπο, όχι από λευκή σελίδα.',
  'onboarding.step.policies.create.activePolicies_one':
    '{{count}} ενεργή πολιτική.',
  'onboarding.step.policies.create.activePolicies_other':
    '{{count}} ενεργές πολιτικές.',

  'onboarding.step.evidence.attach.title':
    'Επισυνάψτε τεκμήρια στις απαντήσεις σας',
  'onboarding.step.evidence.attach.why':
    'Μια απάντηση χωρίς τεκμήριο είναι απλή δήλωση. Το τεκμήριο την κάνει αποδεκτή από ελεγκτή — και το σύστημα παρακολουθεί πότε λήγει.',
  'onboarding.step.evidence.attach.evidenceCount_one':
    '{{count}} τεκμήριο επισυναπτόμενο.',
  'onboarding.step.evidence.attach.evidenceCount_other':
    '{{count}} τεκμήρια επισυναπτόμενα.',

  'onboarding.step.soa.scope.title':
    'Γράψτε το πεδίο εφαρμογής του ΣΔΑΠ',
  'onboarding.step.soa.scope.why':
    'Ένα SoA που δεν λέει σε τι αναφέρεται δεν είναι αυτόνομο έγγραφο. Μια ή δύο προτάσεις αρκούν.',
  'onboarding.step.soa.scope.scopeWritten':
    'Το πεδίο εφαρμογής έχει καταγραφεί.',

  'onboarding.step.soa.decide.title':
    'Αποφασίστε και αιτιολογήστε κάθε δικλίδα του Παραρτήματος A',
  'onboarding.step.soa.decide.why':
    'Ρήτρα 6.1.3(d). Κανένα πιστοποιητικό δεν εκδίδεται χωρίς αυτό το έγγραφο, και μια απόφαση χωρίς αιτιολόγηση είναι το συχνότερο εύρημα των ελεγκτών.',
  'onboarding.step.soa.decide.soaNone_one':
    '{{count}} δικλίδα, καμία απόφαση ακόμα.',
  'onboarding.step.soa.decide.soaNone_other':
    '{{count}} δικλίδες, καμία απόφαση ακόμα.',
  'onboarding.step.soa.decide.soaUndecided_one':
    '{{decided}} από {{total}} αποφασίστηκαν — μένει {{undecided}}.',
  'onboarding.step.soa.decide.soaUndecided_other':
    '{{decided}} από {{total}} αποφασίστηκαν — μένουν {{undecided}}.',
  'onboarding.step.soa.decide.soaUnjustified_one':
    'Και οι {{total}} αποφασίστηκαν, αλλά {{unjustified}} δεν έχει γραπτή αιτιολόγηση.',
  'onboarding.step.soa.decide.soaUnjustified_other':
    'Και οι {{total}} αποφασίστηκαν, αλλά {{unjustified}} δεν έχουν γραπτή αιτιολόγηση.',
  'onboarding.step.soa.decide.soaDone_one':
    '{{count}} δικλίδα αποφασισμένη και αιτιολογημένη.',
  'onboarding.step.soa.decide.soaDone_other':
    'Και οι {{count}} δικλίδες αποφασισμένες και αιτιολογημένες.',

  'onboarding.step.trust.publish.title':
    'Δημοσιεύστε το Trust Centre σας',
  'onboarding.step.trust.publish.why':
    'Μια σελίδα που στέλνετε αντί να συμπληρώνετε το επόμενο ερωτηματολόγιο στο χέρι. Εσείς επιλέγετε τι δείχνει.',
  'onboarding.step.trust.publish.trustLive': 'Ενεργή στο /trust/{{slug}}',
  'onboarding.step.trust.publish.trustDraft':
    'Ρυθμισμένο, αλλά όχι δημοσιευμένο.',

  'onboarding.step.vendors.add.title':
    'Καταγράψτε τους προμηθευτές από τους οποίους εξαρτάστε',
  'onboarding.step.vendors.add.why':
    'Δεν μπορείτε να αξιολογήσετε ή να παρακολουθήσετε έναν προμηθευτή που το σύστημα δεν γνωρίζει.',
  'onboarding.step.vendors.add.vendorCount_one':
    '{{count}} ενεργός προμηθευτής.',
  'onboarding.step.vendors.add.vendorCount_other':
    '{{count}} ενεργοί προμηθευτές.',

  'onboarding.step.vendors.assess.title':
    'Στείλτε ερωτηματολόγιο στους κρίσιμους προμηθευτές',
  'onboarding.step.vendors.assess.why':
    'Οι απαντήσεις τους στηρίζουν τις δικές σας υποχρεώσεις για την εφοδιαστική αλυσίδα. Απαντούν μέσω συνδέσμου, χωρίς λογαριασμό.',
  'onboarding.step.vendors.assess.vendorAssessDone_one':
    '{{count}} ερωτηματολόγιο απαντήθηκε.',
  'onboarding.step.vendors.assess.vendorAssessDone_other':
    '{{count}} ερωτηματολόγια απαντήθηκαν.',
  'onboarding.step.vendors.assess.vendorAssessAwaiting_one':
    '{{count}} απεσταλμένο, χωρίς απάντηση ακόμα.',
  'onboarding.step.vendors.assess.vendorAssessAwaiting_other':
    '{{count}} απεσταλμένα, χωρίς απάντηση ακόμα.',
  'onboarding.step.vendors.assess.vendorAssessMixed_one':
    '{{answered}} απαντήθηκαν, {{awaiting}} εκκρεμεί.',
  'onboarding.step.vendors.assess.vendorAssessMixed_other':
    '{{answered}} απαντήθηκαν, {{awaiting}} εκκρεμούν.',

  'onboarding.step.scans.run.title':
    'Εκτελέστε εξωτερικό έλεγχο σε ένα χωρίο',
  'onboarding.step.scans.run.why':
    'Μια παθητική ματιά σε ό,τι εκθέτει ένα χωρίο — πιστοποιητικά, κεφαλίδες, DNS. Κοστίζει μονάδες, οπότε ξεκινήστε από ένα που μετράει.',
  'onboarding.step.scans.run.scansReady_one': '{{count}} αναφορά έτοιμη.',
  'onboarding.step.scans.run.scansReady_other': '{{count}} αναφορές έτοιμες.',
  'onboarding.step.scans.run.scansRunning_one': '{{count}} έλεγχος σε εξέλιξη.',
  'onboarding.step.scans.run.scansRunning_other': '{{count}} έλεγχοι σε εξέλιξη.',

  'onboarding.step.clients.create.title':
    'Προσθέστε τον πρώτο σας πελάτη',
  'onboarding.step.clients.create.why':
    'Σχεδόν ό,τι κάνετε εδώ γίνεται μέσα στον οργανισμό του πελάτη και όχι στον δικό σας.',
  'onboarding.step.clients.create.clientCount_one':
    '{{count}} οργανισμός-πελάτης.',
  'onboarding.step.clients.create.clientCount_other':
    '{{count}} οργανισμοί-πελάτες.',

  'onboarding.step.clients.consent.title':
    'Εξασφαλίστε τη συγκατάθεση κάθε πελάτη',
  'onboarding.step.clients.consent.why':
    'Το ότι βρίσκεστε πάνω από έναν πελάτη σας επιτρέπει να τον χρεώνετε. Δεν σας επιτρέπει να διαβάζετε αξιολογήσεις, κενά ή τεκμήρια — αυτό το παραχωρούν οι ίδιοι και μπορούν να το ανακαλέσουν.',
  'onboarding.step.clients.consent.consentAll_one':
    'Ο ένας πελάτης έχει συγκατατεθεί.',
  'onboarding.step.clients.consent.consentAll_other':
    'Και οι {{count}} πελάτες έχουν συγκατατεθεί.',
  'onboarding.step.clients.consent.consentMissing_one':
    '{{consented}} από {{total}} έχουν συγκατατεθεί — {{missing}} όχι.',
  'onboarding.step.clients.consent.consentMissing_other':
    '{{consented}} από {{total}} έχουν συγκατατεθεί — {{missing}} όχι.',

  'onboarding.step.clients.credits.title':
    'Κατανείμετε μονάδες στους πελάτες σας',
  'onboarding.step.clients.credits.why':
    'Οι άδειες πλαισίων, τα ερωτηματολόγια προμηθευτών και οι έλεγχοι πληρώνονται σε μονάδες. Πελάτης χωρίς μονάδες δεν μπορεί να ξεκινήσει.',
  'onboarding.step.clients.credits.creditsAll_one':
    'Ο ένας πελάτης έχει μονάδες.',
  'onboarding.step.clients.credits.creditsAll_other':
    'Και οι {{count}} πελάτες έχουν μονάδες.',
  'onboarding.step.clients.credits.creditsMissing_one':
    '{{funded}} από {{total}} έχουν μονάδες — {{missing}} όχι.',
  'onboarding.step.clients.credits.creditsMissing_other':
    '{{funded}} από {{total}} έχουν μονάδες — {{missing}} όχι.',

  'onboarding.step.team.invite.title':
    'Προσκαλέστε τους ανθρώπους που θα κάνουν τη δουλειά',
  'onboarding.step.team.invite.why':
    'Η συμμόρφωση δεν είναι δουλειά ενός ανθρώπου. Τα σχέδια θέλουν υπεύθυνους και οι πολιτικές εγκρίνοντες.',
  'onboarding.step.team.invite.teamCount_one':
    'Είστε ο μοναδικός χρήστης εδώ.',
  'onboarding.step.team.invite.teamCount_other':
    '{{count}} άτομα σε αυτόν τον οργανισμό.',
  'onboarding.step.team.invite.teamAlone':
    'Είστε ο μοναδικός χρήστης εδώ.',

  'onboarding.step.account.totp.title':
    'Περάστε σε εφαρμογή αυθεντικοποίησης',
  'onboarding.step.account.totp.why':
    'Ο λογαριασμός σας έχει ήδη δεύτερο παράγοντα — κωδικούς μέσω email. Η εφαρμογή αυθεντικοποίησης είναι η ισχυρότερη επιλογή και δουλεύει ακόμα κι όταν το email δεν δουλεύει.',
  'onboarding.step.account.totp.totpOn':
    'Χρησιμοποιείται εφαρμογή αυθεντικοποίησης.',
  'onboarding.step.account.totp.totpEmail':
    'Χρησιμοποιούνται κωδικοί μέσω email.',

  'onboarding.references.title': 'Αξίζει να τα ξέρετε',
  'onboarding.references.note':
    'Δεν ανήκουν στη λίστα — απλώς πού βρίσκονται.',
  'onboarding.reference.incidents.title':
    'Πού καταγράφεται ένα περιστατικό',
  'onboarding.reference.incidents.body':
    'Το άρθρο 23 της NIS2 δίνει 24 ώρες για την πρώιμη προειδοποίηση. Το να ξέρετε την οθόνη από πριν είναι όλο το νόημα.',
  'onboarding.reference.catalogue.title':
    'Αποδείξτε μία φορά, καλύψτε πολλά',
  'onboarding.reference.catalogue.body':
    'Μια δικλίδα μπορεί να καλύπτει απαιτήσεις πολλών πλαισίων. Η βιβλιοθήκη δικλίδων δείχνει ποιες.',
  'onboarding.reference.reports.title':
    'Εξαγωγές για τον ελεγκτή σας',
  'onboarding.reference.reports.body':
    'Το Πεδίο Εφαρμογής και οι αναφορές αξιολόγησης κατεβαίνουν από εδώ.',
  'onboarding.reference.vendorAssessments.title':
    'Ερωτηματολόγια που έχετε στείλει',
  'onboarding.reference.vendorAssessments.body':
    'Τι έχετε ζητήσει από προμηθευτές, τι έχει επιστραφεί και τι έχει λήξει.',
};

let registered = false;

/**
 * Register both tables with the running i18n instance.
 *
 * Idempotent, and `addResources` never overwrites an existing key — so once
 * these land in en.json and el.json the bundled strings win and this becomes a
 * no-op rather than a second source of truth.
 */
export function registerOnboardingTranslations(): void {
  if (registered) return;
  registered = true;
  for (const [lng, table] of [
    ['en', ONBOARDING_STRINGS],
    ['el', ONBOARDING_STRINGS_EL],
  ] as const) {
    i18n.addResources(lng, 'translation', table as Record<string, string>);
  }
}
registerOnboardingTranslations();

/**
 * Lookup helper with the English table as the inline fallback.
 *
 * `tx` for a plain string, `txn` for one whose wording depends on a number.
 * The server supplies `count` alongside the named variables precisely so
 * i18next can pick the plural form — see the note where `count` is set in the
 * API's resolvers.
 */
export function useOnboardingT() {
  const { t } = useTranslation();

  const tx = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      t(key, {
        defaultValue: ONBOARDING_STRINGS[key as OnboardingKey] ?? key,
        ...options,
      }) as string,
    [t],
  );

  const txn = useCallback(
    (key: string, vars: Record<string, string | number>): string => {
      const count = typeof vars.count === 'number' ? vars.count : undefined;
      const suffix = count === 1 ? '_one' : '_other';
      const fallback =
        ONBOARDING_STRINGS[`${key}${suffix}` as OnboardingKey] ??
        ONBOARDING_STRINGS[key as OnboardingKey] ??
        key;
      return t(key, { ...vars, defaultValue: fallback }) as string;
    },
    [t],
  );

  return { tx, txn };
}
